import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import { Provider } from 'react-redux';
import store from './redux/store';

// Critical CSS only - loads immediately
import './styles/scss/critical.scss';
// Leaflet CSS - needed early for maps
import 'leaflet/dist/leaflet.css';

// Lazy load non-critical CSS after initial render
const loadNonCriticalCSS = () => {
  import('./styles/scss/non-critical.scss');
  import('./styles/scss/style.scss');
};

// Load non-critical CSS after a short delay
setTimeout(loadNonCriticalCSS, 100);
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { showNetworkError, formatMsalAuthError } from './utils/generalUtils';

import { getCurrentActiveProjectId, clearCurrentActiveProjectId } from './utils/projectUtils.js';
import { Exceptionless, ExceptionlessErrorBoundary } from "@exceptionless/react";
import { logException, logInfo } from './services/LoggingService.js';
import { clearCachedAccessTokenProps, setAccessToken, setRefreshToken, setUserName, setTenantName, setExpirationAccessToken, setExpirationRefreshToken, getUserName, getTenantName, setAvailableTenants, setTenantSelectionPending } from './utils/tokenUtils.js';
import { fetchAvailableTenants } from './utils/tenantSwitchUtils.js';
import { BadDogAuthHandoffFailure, handoffBadDogAuthToken } from './services/badDogAuthHandoff.js';

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig);

const MSAL_AUTH_FINGERPRINT_KEY = 'telescope_msal_auth_fingerprint';

function getMsalAuthFingerprint() {
    return `${msalConfig.auth.clientId}|${msalConfig.auth.authority}`;
}

async function clearMsalCacheIfAuthConfigChanged(instance) {
    const fingerprint = getMsalAuthFingerprint();
    const previous = sessionStorage.getItem(MSAL_AUTH_FINGERPRINT_KEY);
    const msalKeys = Object.keys(sessionStorage).filter((k) => k.toLowerCase().includes('msal'));
    if (!previous && msalKeys.length > 0) {
        console.warn('MSAL cache from a prior Entra configuration; clearing before sign-in.');
        await instance.clearCache();
    } else if (previous && previous !== fingerprint) {
        console.warn('MSAL auth config changed; clearing cached Entra session.');
        await instance.clearCache();
    }
    sessionStorage.setItem(MSAL_AUTH_FINGERPRINT_KEY, fingerprint);
}

//=================================================================================

// Initialize the MSAL instance
async function initializeMsal() {
  try {
      await msalInstance.initialize();
      await clearMsalCacheIfAuthConfigChanged(msalInstance);
      console.log("=====================ENTRA EVENT - initializeMsal() ===============================");
      console.log("MSAL initialized successfully");
      console.log("MSAL authority:", msalConfig.auth.authority);
      console.log("MSAL clientId:", msalConfig.auth.clientId);
      console.log("MSAL redirectUri:", msalConfig.auth.redirectUri);

      // Handle redirect response (failures reject here after workforce/CIAM migration)
      const response = await msalInstance.handleRedirectPromise();
      if (response?.account) {
          console.log("ENTRA redirect login succeeded for:", response.account.username);
      }
      console.log("ENTRA Redirect promise handled");
      
      console.log("=====================ENTRA EVENT - initializeMsal() =============================");
    } catch (error) {
      console.error("Error initializing MSAL: ", error);
      showNetworkError("LOGIN FAILED at MS-Entra", formatMsalAuthError(error));
  }
}

initializeMsal();


//=================================================================================


// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Listen for sign-in event and set active account
msalInstance.addEventCallback(async (event) => {

    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {

        const account = event.payload.account;
        msalInstance.setActiveAccount(account);
        
        // Set username from MSAL immediately to avoid race condition with UI components
        setUserName(account.username);
        
        Exceptionless.config.setUserIdentity(account.username, account.name);
        logInfo(`USER SIGN IN succeeded for userName = ${account.username} (${account.name}) `, 'MS-ENTRA Authentication');


        // If there is an active project stored in localStorage, we need to clear it
        // (because it sometimes causes errors in DEV/TEST cycles when it remembers a project that no longer exists -- after a DB refresh)        
        clearCurrentActiveProjectId();

        console.log("=====================ENTRA EVENT - MAIN.JSX call back =============================");
        console.log("`main.jsx` EVENT CALLBACK: ", event);

        if(event.payload.idToken) {

          const exp = event.payload.idTokenClaims.exp;
          const now = Math.floor(Date.now() / 1000); // Current time in seconds

          var timeDifference = exp - now;
          var differenceDate = new Date(timeDifference * 1000);
          var diffHours = differenceDate.getUTCHours();
          var diffMinutes = differenceDate.getUTCMinutes();
          var diffSeconds = differenceDate.getUTCSeconds();
          var readableDifference = diffHours + ':' + diffMinutes + ':' + diffSeconds;

          if (exp < now) {
            console.warn("`main.jsx` ID Token is expired, redirecting to signout page");  
          }

          console.log("==================================================================");
          console.log("`main.jsx` ID Token:", event.payload.idToken);
          console.log("(calling BADDOG auth proxy to get an ACCESS TOKEN for the DataAPI in exchange for the ID Token)");
          console.log("==================================================================");

          try {
            const responseFromBadDog = await handoffBadDogAuthToken(event.payload.idToken);

            // TODO: change this caching to use a HTTP-ONLY COOKIE instead of localStorage
            setAccessToken(responseFromBadDog.access_token );
            setRefreshToken(responseFromBadDog.refresh_token );

            // Check if user has access to multiple tenants; if so, prompt to choose one
            try {
                const tenants = await fetchAvailableTenants();
                setAvailableTenants(tenants);
                if (Array.isArray(tenants) && tenants.length > 1) {
                    setTenantSelectionPending(true);
                }
                // Notify navbar to re-read available tenants
                window.dispatchEvent(new Event('userNameChanged'));
            } catch (tenantFetchError) {
                // Non-fatal — user proceeds as single-tenant
                console.warn('Could not fetch available tenants; proceeding as single-tenant:', tenantFetchError);
            } finally {
                // Unblock the /redirect hold page — all async login work is complete
                window.dispatchEvent(new Event('telescopePostLoginReady'));
            }

            Exceptionless.config.setUserIdentity(getUserName(), account.name);
            logInfo(`USER SIGN IN succeeded for userName = ${getUserName()} (${account.name}) `, 'BADDOG AuthHandoff');
    
            // localStorage.setItem('BADDOG-refreshToken', responseFromBadDog.refresh_token );

          } catch (error) {
            console.error("Error fetching ACCESS_TOKEN on handoff call to BADDOG: ", error);
            logException(error);
            clearCachedAccessTokenProps();

            if (error.kind === BadDogAuthHandoffFailure.ACCOUNT_NOT_CONFIGURED) {
              window.location = "/ErrorAccountNotConfigured";
              return;
            }

            window.location = "/authorization-service-unavailable";
          }


        } else {
          if (event.eventType === EventType.LOGIN_SUCCESS ) {
            console.warn("==================================================================");
            console.warn("`main.jsx` NO ID Token is present on LOGIN_SUCCESS event CALLBACK!!!");
            console.warn("==================================================================");
            
            // If there is an active project stored in localStorage, we need to clear it
            // (because it sometimes causes errors in DEV/TEST cycles when it remembers a project that no longer exists -- after a DB refresh)        
            clearCurrentActiveProjectId();

          } else {
            console.warn("`main.jsx` MSAL EVENT type " + event.eventType + " CALLBACK!!!");
          }

        }
    }

    if (event.eventType === EventType.LOGIN_FAILURE) {
        console.error("`main.jsx` LOGIN_FAILURE event: ", event);
        showNetworkError(
            "LOGIN FAILED at MS-Entra",
            formatMsalAuthError(event.error || event.payload)
        );
    }

    if (event.eventType === EventType.LOGOUT_START) {

        const account = msalInstance.getActiveAccount();
        const userName = getUserName() ? getUserName() : "unknown";
        const name = account ? account.name : "unknown";

        console.log("`main.jsx` LOGOUT_START event: ", event);
        logInfo(`LOGOUT started for userName = ${userName} (${name})`, 'MS-ENTRA Authentication');
        Exceptionless.processQueue();
    }

});


ReactDOM.createRoot(document.getElementById('root')).render(
  // <React.StrictMode>
  <Provider store={store} >
    <App instance={msalInstance}/>
  </Provider>
  // </React.StrictMode>,
)
